import { FC } from 'react'
import Select from '@UI/New/Select'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import { TeacherSalaryType } from '../../types'
import {
  TeacherStudentRateInput,
  TEACHER_SALARY_OPTIONS,
  getTeacherSalaryTooltip,
  isThresholdSalary,
  PAID_CANCEL_SALARY_LABEL,
  PAID_CANCEL_SALARY_DESCRIPTION,
} from '../../utils/teacherSalary'
import TeacherSalaryScheduleFields from './TeacherSalaryScheduleFields'
import cn from './index.module.css'

const PER_STUDENT_HOUR_TOOLTIP =
  'Если расчёт по человеко-часам меньше указанной суммы, педагог получит минимальную сумму за занятие.'
const PER_GROUP_STUDENT_HINT =
  'Можете указать ЗП педагога в зависимости от того, сколько учащихся было в группе на момент отчета за занятие. (Именно сколько было в группе, а не сколько пришло на занятие). При большем кол-ве берется наибольшая ставка'

export interface SalaryFormValues {
  salaryType: TeacherSalaryType
  salary: string
  salaryDurationMinutes: string
  minAmountEnabled: boolean
  minAmount: string
  paidCancelPercent: string
  studentRates: TeacherStudentRateInput[]
}
interface Props {
  value: SalaryFormValues
  onChange: (values: Partial<SalaryFormValues>) => void
  lessonPayment: boolean
}
const TeacherSalaryFields: FC<Props> = ({ value, onChange, lessonPayment }) => {
  const threshold = isThresholdSalary(value.salaryType)
  return (
    <div className={cn.salaryFields}>
      <FieldWrapper
        label="Тип зарплаты"
        tooltip={getTeacherSalaryTooltip(value.salaryType)}
      >
        <Select
        className={cn.salarySelect}
          compact
          fields={{
            value: value.salaryType,
            options: TEACHER_SALARY_OPTIONS.filter(
              (option) => lessonPayment || option.value !== 'per-attendee',
            ),
            onChange: (salaryType: TeacherSalaryType) =>
              onChange({
                salaryType,
                studentRates: isThresholdSalary(salaryType)
                  ? value.studentRates.length
                    ? value.studentRates
                    : [{ count: '1', salary: '100' }]
                  : [],
              }),
          }}
        />
      </FieldWrapper>
      {value.salaryType === 'per-duration' && (
        <TextField
          label="Расчётная длительность, минуты"
          type="number"
          min={1}
          size="small"
          value={value.salaryDurationMinutes}
          onChange={(salaryDurationMinutes) =>
            onChange({ salaryDurationMinutes })
          }
        />
      )}
      {!threshold && (
        <TextField
          label={
            value.salaryType === 'per-student-hour'
              ? 'Ставка за человеко-час'
              : value.salaryType === 'per-duration'
              ? `Ставка за занятие (${value.salaryDurationMinutes || '…'} мин.)`
              : 'Ставка за занятие'
          }
          tooltip={
            value.salaryType === 'per-student-hour'
              ? PER_STUDENT_HOUR_TOOLTIP
              : undefined
          }
          type="number"
          min={0}
          size="small"
          value={value.salary}
          onChange={(salary) => onChange({ salary })}
        />
      )}
      {(threshold ||
        (value.salaryType === 'per-lesson' &&
          value.studentRates.length > 0)) && (
        <>
          {value.salaryType === 'per-group-student' && (
            <p className={cn.salaryHint}>{PER_GROUP_STUDENT_HINT}</p>
          )}
          <TeacherSalaryScheduleFields
            thresholds={threshold}
            studentRates={value.studentRates}
            setStudentRates={(update) =>
              onChange({
                studentRates:
                  typeof update === 'function'
                    ? update(value.studentRates)
                    : update,
              })
            }
          />
        </>
      )}
      {value.salaryType === 'per-student-hour' && (
        <>
          <CheckboxLabel
            label="Также выплачивать минимальную сумму"
            value={value.minAmountEnabled}
            onChange={(minAmountEnabled) => onChange({ minAmountEnabled })}
          />
          {value.minAmountEnabled && (
            <TextField
              label="Минимальная сумма"
              type="number"
              min={0}
              size="small"
              value={value.minAmount}
              onChange={(minAmount) => onChange({ minAmount })}
            />
          )}
        </>
      )}
      {lessonPayment && (
        <TextField
          label={PAID_CANCEL_SALARY_LABEL}
          tooltip={PAID_CANCEL_SALARY_DESCRIPTION}
          type="number"
          min={0}
          max={100}
          size="small"
          value={value.paidCancelPercent}
          onChange={(paidCancelPercent) => onChange({ paidCancelPercent })}
        />
      )}
    </div>
  )
}
export default TeacherSalaryFields
